<script lang="ts" setup>
import { EvaluationRelevantInstance } from "types/EvaluationResults";

const props = defineProps({
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
});

const documentId = inject("documentId") as any;

const evalutionResultsStore = useStepResultsStore(documentId);
const {
  criteriaParagraphPositionsData,
  relevantInstances,
  criteriaConfig,
  criteriaInstance,
} = storeToRefs(evalutionResultsStore);

const evaluativeParagraphsStore =
  useDocumentEvaluativeParagraphsStore(documentId);
const { relevantInstanceToParagraphDataMap } = storeToRefs(
  evaluativeParagraphsStore
);

const toggleInstancePosition = (
  paragraphId: string,
  bodyParagraphPosition: number
) => {
  groupedRelevantInstances.value[paragraphId].position = bodyParagraphPosition;
  for (let instance of groupedRelevantInstances.value[paragraphId].instances) {
    if (instance.bodyParagraphPosition == bodyParagraphPosition) {
      evalutionResultsStore.setInstancePosition(instance, undefined);
      return;
    }

    evalutionResultsStore.setInstancePosition(instance, bodyParagraphPosition);
    evalutionResultsStore.saveEvaluationResults();
  }
};

const onUpdateParagraph = (value: number, index: number) => {
  try {
    evalutionResultsStore.updateManualParagraphPoints(index, value);
  } catch (error) {}
};

const { addPositions, removePositions } = useDocumentHighlightStore(documentId);

const documentTokenIdPositionMap =
  useDocumentTokenIdPositionMapStore(documentId);
const { tokenGlobalPositionFromId } = storeToRefs(documentTokenIdPositionMap);

const relevantInstancesToDisplay = ref<EvaluationRelevantInstance[]>([]);

onMounted(() => {
  let copy = [...relevantInstances.value];

  copy.sort((a, b) => (b.confidenceScore ?? 0) - (a.confidenceScore ?? 0));

  // copy = copy.slice(0, numExpectedBodyParagraphs.value);

  copy.sort(
    (a, b) => (a.tokens[0]?.position ?? 0) - (b.tokens[0]?.position ?? 0)
  );

  relevantInstancesToDisplay.value = copy;

  for (var relevantInstance of relevantInstances.value) {
    changePositionHighlightRecursive(
      relevantInstance,
      criteriaConfig.value?.highlightInstances == true,
      false
    );
  }

  for (var relevantInstance of relevantInstancesToDisplay.value) {
    changePositionHighlightRecursive(
      relevantInstance,
      criteriaConfig.value?.highlightInstances == true,
      true
    );
  }
});

const changePositionHighlightRecursive = (
  relevantInstance: EvaluationRelevantInstance,
  highlightInstancesConfig: boolean,
  isHighlighting: boolean
) => {
  if (relevantInstance.type == "paragraph") {
    if (relevantInstance.criteria && relevantInstance.criteria.length > 0) {
      for (var criteria of relevantInstance.criteria) {
        for (var relevantInstance of criteria.relevantInstances) {
          changePositionHighlightRecursive(
            relevantInstance,
            criteria.config.highlightInstances,
            isHighlighting
          );
        }
      }
    }
  }

  if (highlightInstancesConfig == true) {
    var positions = relevantInstance.tokens.map((token) =>
      tokenGlobalPositionFromId.value(token.id)
    );

    var filteredPositions = positions.filter(
      (position) => position != null
    ) as number[];

    if (isHighlighting == true) {
      addPositions(filteredPositions);
    } else {
      removePositions(filteredPositions);
    }
  }
};

const { clearSelectedHighlight, setTokenPositionsToHighlight } =
  useDocumentHighlightStore(documentId);

const paragraphPositionData = computed(() => {
  return (position: number) => {
    return criteriaParagraphPositionsData.value.find(
      (data) => data.position == position
    );
  };
});

const groupedRelevantInstances = computed(() => {
  const paragraphIdToInstancesMap = {} as {
    [key: string]: {
      instances: EvaluationRelevantInstance[];
      position?: number;
      isCustom?: boolean;
    };
  };

  for (let instance of relevantInstances.value) {
    const paragraphId =
      useDocumentEvaluativeParagraphsStore(
        documentId
      ).getParagraphIdForInstance(instance) ?? "undefined";

    if (!paragraphIdToInstancesMap[paragraphId]) {
      paragraphIdToInstancesMap[paragraphId] = {
        instances: [],
      };
    }

    paragraphIdToInstancesMap[paragraphId].instances.push(instance);

    if (instance.bodyParagraphPosition) {
      paragraphIdToInstancesMap[paragraphId].position =
        instance.bodyParagraphPosition;
    }
  }

  let doAllGroupsHavePositions = true;

  const paragraphIds = Object.keys(paragraphIdToInstancesMap);

  let allocatedPositions = [] as number[];

  for (let paragraphId of paragraphIds) {
    const position = paragraphIdToInstancesMap[paragraphId].position;

    if (position == undefined) {
      doAllGroupsHavePositions = false;
    } else {
      allocatedPositions.push(position);
    }
  }

  const unallocatedPositions = criteriaParagraphPositionsData.value.filter(
    (data) => {
      return !allocatedPositions.includes(data.position);
    }
  );

  if (doAllGroupsHavePositions) {
    for (var position of unallocatedPositions) {
      paragraphIdToInstancesMap[`unallocated-${position.position}`] = {
        instances: [],
        position: position.position,
        isCustom: true,
      };
    }
  }

  return paragraphIdToInstancesMap;
});

const sortedParagraphIds = computed(() => {
  const paragraphIds = Object.keys(groupedRelevantInstances.value);

  let doAllGroupsHavePositions = true;

  for (let paragraphId of paragraphIds) {
    const position = groupedRelevantInstances.value[paragraphId].position;

    if (position == undefined) {
      doAllGroupsHavePositions = false;
    }
  }

  return paragraphIds.sort((a, b) => {
    const positionA =
      doAllGroupsHavePositions == true
        ? groupedRelevantInstances.value[a].position ?? 0
        : groupedRelevantInstances.value[a].instances[0]?.position ?? 0;
    const positionB =
      doAllGroupsHavePositions == true
        ? groupedRelevantInstances.value[b].position ?? 0
        : groupedRelevantInstances.value[b].instances[0]?.position ?? 0;

    return positionA - positionB;
  });
});

const paragraphIdBodyPosition = computed(() => {
  return (paragraphId: string) => {
    return groupedRelevantInstances.value[paragraphId].position;
  };
});

const showAll = ref(false);

const paragraphInstances = computed(() => {
  return (paragraphId: string) => {
    let instances = groupedRelevantInstances.value[paragraphId].instances.sort(
      (a, b) => {
        // First check check if one of the instances .valid is true and the other false and sort by that
        if (a.valid && !b.valid) {
          return -1;
        } else if (!a.valid && b.valid) {
          return 1;
        }

        return (b.confidenceScore ?? 0) - (a.confidenceScore ?? 0);
      }
    );

    return instances;
  };
});

const paragraphInstancesToDisplay = computed(() => {
  return (paragraphId: string) => {
    const instances = [...paragraphInstances.value(paragraphId)];

    if (
      showAll.value == true ||
      criteriaConfig.value?.shouldHideExtraInstances != true
    ) {
      return instances;
    }

    return instances.splice(0, 1);
  };
});

const paragraphPosition = computed(() => {
  return (position: number) => {
    return criteriaParagraphPositionsData.value.find(
      (data) => data.position == position
    );
  };
});

const documentViewModeStore = useDocumentViewMode(documentId)();
const { isTeacherViewMode } = storeToRefs(documentViewModeStore);
</script>

<template>
  <div
    class="flex flex-col mb-2 rounded border-2 px-4 py-4"
    v-if="criteriaConfig"
  >
    <div v-if="isTeacherViewMode" class="grow overflow-auto min-h-0">
      <div
        v-for="paragraphId in sortedParagraphIds"
        :key="paragraphId"
        class="mb-4"
      >
        <div class="flex flex-row justify-between items-center border-b pb-2">
          <div class="w-[250px]">
            <BaseSelectInput
              v-if="groupedRelevantInstances[paragraphId].isCustom != true"
              :model-value="paragraphIdBodyPosition(paragraphId)"
              placeholder="Select paragraph"
              :options="criteriaParagraphPositionsData"
              value-prop="position"
              label="longLabel"
              @update:modelValue="toggleInstancePosition(paragraphId, $event)"
            />
            <div
              v-if="
                groupedRelevantInstances[paragraphId].isCustom == true &&
                paragraphPosition(
                  groupedRelevantInstances[paragraphId].position!
                ) != undefined
              "
              class="grow mr-2 font-bold select-none flex flex-row items-center -mt-[1px] leading-[30px]"
            >
              {{
                paragraphPosition(
                  groupedRelevantInstances[paragraphId].position!
                )?.longLabel
              }}
            </div>
          </div>

          <BaseScoreInput
            v-if="paragraphIdBodyPosition(paragraphId) != undefined"
            @click.stop.prevent
            @input="
              onUpdateParagraph($event, paragraphIdBodyPosition(paragraphId)!)
            "
            :key="paragraphIdBodyPosition(paragraphId)"
            :value="
              paragraphPositionData(paragraphIdBodyPosition(paragraphId)!)
                ?.manualScore ??
              paragraphPositionData(paragraphIdBodyPosition(paragraphId)!)
                ?.score ??
              0
            "
            :tabIndex="(parseInt(paragraphIdBodyPosition(paragraphId)! as any) + 1) * 10"
            :width="40"
            :height="30"
            :max-points="criteriaInstance.pointsPerInstance"
            :show-points-label="true"
          />
        </div>

        <div
          v-if="
            groupedRelevantInstances[paragraphId].position &&
            groupedRelevantInstances[paragraphId].isCustom &&
            groupedRelevantInstances[paragraphId].instances.length == 0
          "
          class="px-2 py-2"
        >
          <DocumentPanelChecklistParagraphNoneFound
            :position="groupedRelevantInstances[paragraphId].position!"
            :checklist-criteria="checklistCriteria"
          />
        </div>

        <EvaluationResultsInstancesList
          :relevant-instances="paragraphInstancesToDisplay(paragraphId)"
          :criteria-config="criteriaConfig"
          :criteria-instance="criteriaInstance"
          :show-all="showAll"
        />

        <BaseTextButton
          v-if="
            criteriaConfig?.shouldHideExtraInstances &&
            paragraphInstances(paragraphId).length > 1
          "
          color="primary"
          @click="showAll = !showAll"
        >
          {{ showAll ? "Show less" : "Show more" }}
        </BaseTextButton>
      </div>
    </div>

    <div v-if="isTeacherViewMode != true" class="w-full text-center">
      Your document is not formatted correctly. Please resubmit.
    </div>
  </div>
</template>

<style scoped></style>
